import ctl from '@netlify/classnames-template-literals'
import { ValidationError } from '@tanstack/react-form'
import { ReactNode } from 'react'
import { validationErrorMessages } from '../validation-error-message'

export interface GenericFieldProps {
	label: ReactNode
	errors?: Array<ValidationError>
	hint?: ReactNode
	children: ReactNode

	collapsed?: boolean
	collapsible?: boolean
	onToggleCollapse?: (newState: boolean) => void

	topRightTooltip?: ReactNode
	wrapLabel?: boolean
	sizing?: GenericFieldSizing
	hideLabel?: boolean
}

export type GenericFieldSizing = 'small' | 'medium'

export function GenericField(
	{
		label,
		errors,
		hint,
		children,
		collapsible = false,
		collapsed = false,
		onToggleCollapse = () => {},
		topRightTooltip,
		wrapLabel = true,
		sizing = 'medium',
		hideLabel = false,
	}: GenericFieldProps,
) {
	const inner = (
		<>
			{!hideLabel && (
				<div className={ctl(`flex items-center justify-between ${sizing === 'small' ? 'gap-1' : 'gap-2'}`)}>
					<div
						className={ctl(
							`text-grey-900 flex items-center gap-2 ${sizing === 'small' ? 'text-sm' : 'text-base'}`,
						)}
					>
						{label}
						{collapsible && (
							<button
								type="button"
								className={ctl(
									`scale-75 animate-all ${collapsed ? 'text-grey-400' : 'text-grey-900 rotate-90'}`,
								)}
								onClick={() => onToggleCollapse(!collapsed)}
							>
								<ArrowIcon />
							</button>
						)}
					</div>
					{(topRightTooltip && !collapsed) && <div className="">{topRightTooltip}</div>}
				</div>
			)}
			{collapsed ? null : <div className="flex items-center justify-stretch gap-2">{children}</div>}
			{validationErrorMessages(errors).map((message, index) => (
				<div className="text-red-500 text-sm" key={index}>{message}</div>
			))}
			{hint ? <div className="text-grey-700 text-sm">{hint}</div> : null}
		</>
	)

	if (wrapLabel) {
		return (
			<label className="flex flex-col gap-2 min-w-[150px]">
				{inner}
			</label>
		)
	}

	return (
		<div className="flex flex-col gap-2 min-w-[200px]">
			{inner}
		</div>
	)
}

function ArrowIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 14 14"
			id="Tailless-Triangle-Arrow-Right-1--Streamline-Core"
			height={14}
			width={14}
		>
			<desc>{'\n    Tailless Triangle Arrow Right 1 Streamline Icon: https://streamlinehq.com\n  '}</desc>
			<g id="tailless-triangle-arrow-right-1--arrow-right-keyboard">
				<path
					id="Union"
					fill="currentColor"
					fillRule="evenodd"
					d="M5.20711 0.292922c-0.286 -0.28599756 -0.71612 -0.3715533 -1.08979 -0.2167723C3.74364 0.230931 3.5 0.595567 3.5 1.00003V13c0 0.4045 0.24364 0.7691 0.61732 0.9239 0.37367 0.1548 0.80379 0.0692 1.08979 -0.2168l5.64649 -5.64641c0.5857 -0.58579 0.5857 -1.53553 0 -2.12132L5.20711 0.292922Z"
					clipRule="evenodd"
					strokeWidth={1}
				/>
			</g>
		</svg>
	)
}
